import { normalise } from '../../shared/access/accessModel';

/**
 * Real faces in the admin lists.
 *
 * `/admin/users` answers with identity and licensing only — no picture, under
 * any of the nine spellings `accessModel.normalise` knows. So every row in
 * Administration › Users rendered the initials disc, for every person, even
 * the ones whose photo the rest of the app shows: the same colleague was a
 * portrait on the People page and "SM" here.
 *
 * The pictures do exist — `/org-people/people` carries `profile_picture` for
 * everyone in the organization (it is what `useOrgMembers` reads). This is the
 * join: build an index of that payload once per page, then look a person up by
 * whichever key the admin row happens to carry.
 *
 * Deliberately pure and key-order-explicit: a picture that resolves for the
 * wrong person is worse than no picture, so what may be matched on is stated
 * here rather than inferred at each call site.
 */

/** Lookup keys → picture. One entry per spelling of the same person. */
export type AvatarDirectory = ReadonlyMap<string, string>;

export const EMPTY_AVATAR_DIRECTORY: AvatarDirectory = new Map<string, string>();

const clean = (value: unknown): string =>
  typeof value === 'string' ? value.trim().toLowerCase() : '';

/**
 * Every handle a person might be recognised by, most specific first.
 *
 * Email and the id/uuid family identify one person exactly. The display name
 * does not — two people can share one — so it is only ever used as the LAST
 * key, and only because the audit log identifies whoever acted by name alone.
 * A collision there shows the wrong photo for a namesake; the alternative is
 * an anonymous glyph for everyone, and the row still names the person.
 */
export const directoryKeys = (person: any): string[] => {
  const source = person || {};
  const composed = [source.first_name, source.last_name].filter(Boolean).join(' ');
  return [
    clean(source.email || source.Email || source.user_email),
    clean(source.uuid || source.user_uuid || source.UserUID),
    source.id != null ? `id:${clean(String(source.id))}` : '',
    clean(source.name || source.full_name || source.user_name || composed),
  ].filter(Boolean);
};

/**
 * Index a people payload by every handle each person answers to.
 *
 * First writer wins per key: the payload is ordered by the backend, and a
 * later namesake must not take over an earlier person's key.
 */
export const buildAvatarDirectory = (people: any[] | null | undefined): AvatarDirectory => {
  const directory = new Map<string, string>();
  for (const raw of people || []) {
    const avatar = normalise(raw).avatar;
    if (!avatar) continue;
    for (const key of directoryKeys(raw)) {
      if (!directory.has(key)) directory.set(key, avatar);
    }
  }
  return directory;
};

/**
 * The picture for one person: whatever their own record carries, and only
 * failing that, the directory.
 *
 * That order matters — if `/admin/users` ever starts sending pictures, the
 * row's own data must win over a second endpoint's copy of it.
 */
export const avatarFor = (
  person: any,
  directory: AvatarDirectory = EMPTY_AVATAR_DIRECTORY
): string | null => {
  const own = normalise(person).avatar;
  if (own) return own;
  for (const key of directoryKeys(person)) {
    const found = directory.get(key);
    if (found) return found;
  }
  return null;
};
